import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ACCOMMODATION_LABELS,
  ACCOMMODATION_TYPES,
  BOOKING_STATUSES,
} from '@greenostrov/shared';
import type { AccommodationType, BookingStatus } from '@greenostrov/shared';
import { api } from '../lib/api';
import { BOOKING_STATUS_LABELS } from '../lib/labels';
import type { BookingRow } from '../lib/types';

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.getBooking(id!),
    enabled: Boolean(id),
  });

  const [form, setForm] = useState<Partial<BookingRow>>({});

  useEffect(() => {
    if (query.data) setForm(query.data as unknown as BookingRow);
  }, [query.data]);

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateBooking(id!, patch),
    onSuccess: (data) => {
      qc.setQueryData(['booking', id], data);
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteBooking(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      navigate('/bookings');
    },
  });

  if (query.isLoading) return <div>Загрузка…</div>;
  if (query.error) return <div className="error">Ошибка: {(query.error as Error).message}</div>;
  if (!form.id) return null;

  const b = form as BookingRow;

  function onChange<K extends keyof BookingRow>(key: K, value: BookingRow[K] | null | string) {
    setForm((f) => ({ ...f, [key]: value as BookingRow[K] }));
  }

  function save() {
    if (!query.data) return;
    const original = query.data as unknown as BookingRow;
    const patch: Record<string, unknown> = {};
    const keys: (keyof BookingRow)[] = [
      'name',
      'phone',
      'email',
      'accommodationType',
      'checkin',
      'checkout',
      'guests',
      'comment',
      'status',
      'priceTotal',
      'adminNotes',
    ];
    for (const key of keys) {
      if (form[key] !== original[key]) patch[key] = form[key] ?? null;
    }
    if (Object.keys(patch).length === 0) return;
    update.mutate(patch);
  }

  return (
    <div>
      <Link to="/bookings" style={{ color: 'var(--text-muted)', fontSize: 14 }}>
        ← К списку заявок
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24 }}>{b.name}</h1>
        <span className={`badge badge-${b.status}`}>{BOOKING_STATUS_LABELS[b.status]}</span>
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 13 }}>
        Создано {new Date(b.createdAt).toLocaleString('ru')} · ID {b.id.slice(0, 8)}
      </p>

      <div className="card" style={{ padding: 24, display: 'grid', gap: 16 }}>
        <Row label="Статус">
          <select
            className="select"
            value={b.status}
            onChange={(e) => onChange('status', e.target.value as BookingStatus)}
          >
            {BOOKING_STATUSES.map((s) => (
              <option key={s} value={s}>
                {BOOKING_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Row>

        <Grid>
          <Row label="Имя">
            <input className="input" value={b.name} onChange={(e) => onChange('name', e.target.value)} />
          </Row>
          <Row label="Телефон">
            <input className="input" value={b.phone} onChange={(e) => onChange('phone', e.target.value)} />
          </Row>
          <Row label="Email">
            <input
              className="input"
              value={b.email ?? ''}
              onChange={(e) => onChange('email', e.target.value || null)}
            />
          </Row>
          <Row label="Категория">
            <select
              className="select"
              value={b.accommodationType ?? ''}
              onChange={(e) => onChange('accommodationType', (e.target.value || null) as AccommodationType | null)}
            >
              <option value="">Не указана</option>
              {ACCOMMODATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ACCOMMODATION_LABELS[t]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Заезд">
            <input
              className="input"
              type="date"
              value={b.checkin ?? ''}
              onChange={(e) => onChange('checkin', e.target.value || null)}
            />
          </Row>
          <Row label="Выезд">
            <input
              className="input"
              type="date"
              value={b.checkout ?? ''}
              onChange={(e) => onChange('checkout', e.target.value || null)}
            />
          </Row>
          <Row label="Гостей">
            <input
              className="input"
              type="number"
              min={1}
              max={30}
              value={b.guests ?? ''}
              onChange={(e) => onChange('guests', e.target.value ? Number(e.target.value) : null)}
            />
          </Row>
          <Row label="Сумма (₽)">
            <input
              className="input"
              type="number"
              min={0}
              value={b.priceTotal ?? ''}
              onChange={(e) => onChange('priceTotal', e.target.value ? Number(e.target.value) : null)}
            />
          </Row>
        </Grid>

        <Row label="Комментарий гостя">
          <textarea
            className="textarea"
            rows={3}
            value={b.comment ?? ''}
            onChange={(e) => onChange('comment', e.target.value || null)}
          />
        </Row>

        <Row label="Примечания администратора">
          <textarea
            className="textarea"
            rows={4}
            value={b.adminNotes ?? ''}
            onChange={(e) => onChange('adminNotes', e.target.value || null)}
          />
        </Row>

        {update.error && <div className="error">Ошибка: {(update.error as Error).message}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              if (confirm('Отменить заявку?')) del.mutate();
            }}
          >
            Отменить заявку
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
      {children}
    </div>
  );
}
